import React, { useEffect, useState } from 'react';
import { FaShoppingCart, FaBars, FaTimes } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAudio } from '../context/AudioContext'; // Import the custom hook
import { useDispatch, useSelector } from 'react-redux';
import { deepPurple } from "@mui/material/colors";
import { Avatar, Menu, MenuItem } from '@mui/material';
import { getUser, logout } from '../Redux/Auth/Action';
import { getCart } from '../Redux/Customers/Cart/Action';
import { Loader } from './Loader';

const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const jwt = localStorage.getItem("jwt");

  const { isPlaying, toggleMusic } = useAudio();
  const { auth, cart } = useSelector((store) => store);

  useEffect(() => {
    if (jwt) {
      setLoading(true);
      dispatch(getCart(jwt))
        .finally(() => setLoading(false));
    }
  }, [jwt]);

  useEffect(() => {
    if (jwt) {
      setLoading(true);
      dispatch(getUser(jwt))
        .finally(() => setLoading(false));
    }
  }, [jwt]);

  const handleUserClick = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const openUserMenu = Boolean(anchorEl);
  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const playSoundAndNavigate = (path) => {
    const audio = new Audio('/audio2.wav');
    audio.play();
    setTimeout(() => {
      navigate(path);
    }, 1000);
  };

  const handleMyOrderClick = () => {
    handleCloseUserMenu();
    playSoundAndNavigate("/account/order");
  };

  const handleProfileClick = () => {
    handleCloseUserMenu();
    playSoundAndNavigate("/user/profile");
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    dispatch(logout());
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    if (auth?.user?.role === "ADMIN") {
      navigate("/admin");
    }
  }, [auth.user]);

  return (
    <div className="fixed top-0 z-40 w-full h-16 lg:h-32 flex items-center justify-between px-4 lg:px-8 backdrop-blur-md bg-neutral-900 bg-opacity-50">
      <div className="flex items-center h-full">
        <Link to="/" onClick={() => playSoundAndNavigate('/')}>
          <img
            src={"/NanaHanaWhiteText.webp"}
            alt="Logo"
            className="h-10 md:h-12 lg:h-20 object-contain"
          />
        </Link>
      </div>

      <div className="flex items-center space-x-3 lg:hidden">
        <button onClick={toggleMusic} className="text-white text-2xl focus:outline-none">
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        <Link to={"/cart"} className="relative" onClick={() => playSoundAndNavigate('/cart')}>
          <FaShoppingCart className="text-2xl text-white cursor-pointer hover:text-gray-300 transition duration-300" />
          {cart.cart?.totalItem > 0 && (
            <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-sm font-bold px-2 py-1 rounded-full animate-pulse shadow-[0_0_15px_rgba(128,0,128,0.8)]">
              {cart.cart?.totalItem}
            </span>
          )}
        </Link>
        <button onClick={toggleMenu} className="text-white text-2xl focus:outline-none">
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <div className="hidden lg:flex items-center space-x-12 text-lg text-white font-medium">
        <Link to="/categories" onClick={() => playSoundAndNavigate('/categories')} className="hover:text-gray-300 transition duration-300">
          Products
        </Link>
        <Link to="/about-us" onClick={() => playSoundAndNavigate('/about-us')} className="hover:text-gray-300 transition duration-300">
          About Us
        </Link>
        <button
          className="flex items-center hover:text-gray-300 transition duration-300"
          onClick={toggleMusic}
        >
          {isPlaying ? (
            <>
              Pause Music <span className="ml-2">⏸️</span>
            </>
          ) : (
            <>
              Play some music <span className="ml-2">▶️</span>
            </>
          )}
        </button>
      </div>

      <div className="hidden lg:flex items-center space-x-6">
        <Link to={"/cart"} className="relative" onClick={() => playSoundAndNavigate('/cart')}>
          <FaShoppingCart className="text-3xl text-white cursor-pointer hover:text-gray-300 transition duration-300" />
          {cart.cart?.totalItem > 0 && (
            <span className="absolute -top-5 -right-3 bg-purple-600 text-white text-sm font-bold px-2 py-1 rounded-full animate-pulse shadow-[0_0_15px_rgba(128,0,128,0.8)]">
              {cart.cart?.totalItem}
            </span>
          )}
        </Link>
        {auth?.user ? (
          <>
            <Avatar
              className="text-white"
              onClick={handleUserClick}
              aria-controls={openUserMenu ? "basic-menu" : ""}
              aria-haspopup="true"
              aria-expanded={openUserMenu ? "true" : ""}
              sx={{
                bgcolor: deepPurple[500],
                color: "white",
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  boxShadow: "0 0 10px 5px rgba(128, 0, 128, 0.6)",
                },
              }}
            >
              {auth?.user?.firstName ? auth.user.firstName[0].toUpperCase() : "U"}
            </Avatar>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openUserMenu}
              onClose={handleCloseUserMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#1c1c1c",
                  color: "#f0f0f0",
                  borderRadius: "8px",
                  boxShadow: "0 0 15px rgba(0, 0, 0, 0.8)",
                },
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                },
              }}
            >
              <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
              <MenuItem onClick={handleMyOrderClick}>My Orders</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Link to="/sign-up" onClick={() => playSoundAndNavigate('/sign-up')} className="bg-white text-black px-4 py-2 rounded-full hover:bg-gray-200 transition duration-300">
              Sign Up
            </Link>
            <Link to="/sign-in" onClick={() => playSoundAndNavigate('/sign-in')} className="bg-black text-white px-4 py-2 rounded-full hover:bg-gray-800 transition duration-300">
              Sign In
            </Link>
          </>
        )}
      </div>

      {menuOpen && (
        <div className="absolute top-16 left-0 w-full bg-[#0c0c0c] text-white flex flex-col space-y-6 px-8 py-4 lg:hidden h-screen">
          <Link to="/about-us" onClick={() => playSoundAndNavigate('/about-us')} className="hover:text-gray-300">
            About Us
          </Link>
          {auth?.user ? (
            <>
              <Link to="/user/profile" onClick={() => playSoundAndNavigate('/user/profile')}>
                Profile
              </Link>
              <Link to="/account/order" onClick={() => playSoundAndNavigate('/account/order')}>
                My Orders
              </Link>
              <button onClick={handleLogout} className="hover:text-gray-300">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/sign-up" onClick={() => playSoundAndNavigate('/sign-up')} className="hover:text-gray-300">
                Sign Up
              </Link>
              <Link to="/sign-in" onClick={() => playSoundAndNavigate('/sign-in')} className="hover:text-gray-300">
                Sign In
              </Link>
            </>
          )}
        </div>
      )}

      {loading && <Loader />}
    </div>
  );
};

export default Navbar;
